import React, { useState, useEffect } from 'react';
import { Heart, Shield, Target, Award, Users, CheckCircle } from 'lucide-react';

const WhyChooseUsSection = () => {
  const [activeReason, setActiveReason] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const reasons = [
    {
      number: "1",
      title: "Hygienic & Safe Environment",
      description: "Equipped with state-of-the-art technology and driven by a commitment to excellence",
      icon: <Shield size={28} />
    },
    {
      number: "2",
      title: "Affordable & Transparent Billing",
      description: "We strive not only to treat illness but to promote lifelong wellness for individuals and families",
      icon: <CheckCircle size={28} />
    },
    {
      number: "3",
      title: "Focus on Recovery and Wellness",
      description: "Our team of highly skilled doctors, nurses, and healthcare professionals work collaboratively",
      icon: <Target size={28} />
    }
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .why-choose-section {
          padding: 8rem 2rem;
          background: linear-gradient(135deg, #F8FAFB 0%, #FFFFFF 50%, #F0F9FF 100%);
          position: relative;
          overflow: hidden;
        }

        .why-choose-container {
          max-width: 1400px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6rem;
          align-items: center;
        }

        /* Left Content */
        .why-choose-content {
          opacity: 0;
          transform: translateX(-50px);
          animation: slideInLeft 1s ease forwards;
        }

        @keyframes slideInLeft {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .section-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.8rem;
          padding: 0.6rem 1.5rem;
          background: transparent;
          border: 2px solid #E86A33;
          border-radius: 50px;
          color: #E86A33;
          font-weight: 700;
          font-size: 0.85rem;
          letter-spacing: 2px;
          text-transform: uppercase;
          margin-bottom: 2rem;
        }

        .badge-icon {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .badge-icon svg {
          stroke: #E86A33;
        }

        .why-choose-title {
          font-family: 'Playfair Display', serif;
          font-size: 3.5rem;
          font-weight: 800;
          color: #1B4965;
          margin-bottom: 3rem;
          line-height: 1.2;
        }

        /* Reasons List */
        .reasons-list {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .reason-item {
          display: grid;
          grid-template-columns: 70px 1fr;
          gap: 2rem;
          align-items: start;
          padding: 2rem;
          background: white;
          border-radius: 20px;
          border: 2px solid transparent;
          transition: all 0.4s ease;
          cursor: pointer;
          position: relative;
        }

        .reason-item:hover {
          background: linear-gradient(135deg, #FFFFFF, #FFF8F5);
          border-color: #E86A33;
          box-shadow: 0 15px 40px rgba(232, 106, 51, 0.15);
          transform: translateX(10px);
        }

        .reason-item::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: linear-gradient(to bottom, #E86A33, #FF9B6E);
          border-radius: 20px 0 0 20px;
          opacity: 0;
          transition: opacity 0.4s ease;
        }

        .reason-item:hover::before {
          opacity: 1;
        }

        /* Number Badge */
        .number-badge {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, #E86A33, #FF9B6E);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 2rem;
          font-weight: 900;
          font-family: 'Playfair Display', serif;
          box-shadow: 0 10px 30px rgba(232, 106, 51, 0.3);
          position: relative;
          flex-shrink: 0;
        }

        .number-badge::before {
          content: '';
          position: absolute;
          inset: -5px;
          border-radius: 50%;
          background: linear-gradient(135deg, #E86A33, #FF9B6E);
          opacity: 0.2;
          z-index: -1;
        }

        .reason-item:hover .number-badge {
          animation: pulse-badge 0.6s ease;
          box-shadow: 0 15px 40px rgba(232, 106, 51, 0.5);
        }

        @keyframes pulse-badge {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        /* Reason Content */
        .reason-content {
          padding-top: 0.5rem;
        }

        .reason-title {
          font-size: 1.5rem;
          font-weight: 800;
          color: #1B4965;
          margin-bottom: 0.8rem;
          line-height: 1.3;
        }

        .reason-description {
          font-size: 1rem;
          line-height: 1.7;
          color: #666;
          font-weight: 400;
        }

        /* Right Image Section */
        .why-choose-image {
          position: relative;
          height: 650px;
          opacity: 0;
          transform: translateX(50px);
          animation: slideInRight 1s ease 0.3s forwards;
        }

        @keyframes slideInRight {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .image-wrapper {
          position: relative;
          height: 100%;
          border-radius: 30px;
          overflow: hidden;
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.15);
        }

        .image-wrapper::before {
          content: '';
          position: absolute;
          inset: 0;
          border: 5px solid rgba(255, 255, 255, 0.5);
          border-radius: 30px;
          z-index: 2;
          pointer-events: none;
        }

        .main-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        /* Medical Hologram Overlay */
        .hologram-overlay {
          position: absolute;
          top: 0;
          right: 0;
          width: 40%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(27, 73, 101, 0.95) 40%);
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }

        .hologram-content {
          width: 100%;
          height: 60%;
          position: relative;
          opacity: 0.9;
        }

        /* Animated Heart Pulse */
        .heart-pulse {
          position: absolute;
          width: 100%;
          height: 100%;
          background: radial-gradient(circle, rgba(232, 106, 51, 0.4) 0%, transparent 70%);
          border-radius: 50%;
          animation: pulse-heart 2s ease-in-out infinite;
        }

        @keyframes pulse-heart {
          0%, 100% { transform: scale(0.9); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }

        /* Medical Icons Floating */
        .floating-icon {
          position: absolute;
          color: #E86A33;
          filter: drop-shadow(0 0 10px rgba(232, 106, 51, 0.5));
          animation: float-icon 3s ease-in-out infinite;
        }

        .floating-icon:nth-child(1) {
          top: 10%;
          left: 20%;
          animation-delay: 0s;
        }

        .floating-icon:nth-child(2) {
          top: 60%;
          left: 10%;
          animation-delay: 1s;
        }

        .floating-icon:nth-child(3) {
          top: 30%;
          right: 15%;
          animation-delay: 2s;
        }

        @keyframes float-icon {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }

        /* Stats Badge */
        .stats-badge {
          position: absolute;
          bottom: 3rem;
          left: 3rem;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          padding: 2rem;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
          z-index: 3;
          display: flex;
          gap: 2rem;
          border: 2px solid rgba(232, 106, 51, 0.2);
        }

        .stat-item {
          text-align: center;
        }

        .stat-number {
          font-size: 2rem;
          font-weight: 900;
          background: linear-gradient(135deg, #E86A33, #FF9B6E);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-family: 'Playfair Display', serif;
          margin-bottom: 0.3rem;
        }

        .stat-label {
          font-size: 0.85rem;
          font-weight: 600;
          color: #1B4965;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        /* Decorative Elements */
        .decorative-dots {
          position: absolute;
          width: 100px;
          height: 100px;
          background-image: radial-gradient(circle, rgba(232, 106, 51, 0.3) 2px, transparent 2px);
          background-size: 20px 20px;
          z-index: 0;
        }

        .dots-top-left {
          top: -20px;
          left: -20px;
        }

        .dots-bottom-right {
          bottom: -20px;
          right: -20px;
        }

        /* Background Orbs */
        .bg-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.15;
          z-index: -1;
          animation: float-orb 20s ease-in-out infinite;
        }

        .orb-1 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(232, 106, 51, 0.4), transparent);
          top: -100px;
          right: -100px;
          animation-delay: 0s;
        }

        .orb-2 {
          width: 350px;
          height: 350px;
          background: radial-gradient(circle, rgba(27, 73, 101, 0.4), transparent);
          bottom: -100px;
          left: -100px;
          animation-delay: 10s;
        }

        @keyframes float-orb {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(20px, -20px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .why-choose-container {
            grid-template-columns: 1fr;
            gap: 4rem;
          }

          .why-choose-image {
            height: 500px;
          }

          .hologram-overlay {
            width: 50%;
          }
        }

        @media (max-width: 768px) {
          .why-choose-section {
            padding: 4rem 1.5rem;
          }

          .why-choose-title {
            font-size: 2.5rem;
          }

          .reason-item {
            grid-template-columns: 60px 1fr;
            gap: 1.5rem;
            padding: 1.5rem;
          }

          .number-badge {
            width: 60px;
            height: 60px;
            font-size: 1.5rem;
          }

          .reason-title {
            font-size: 1.2rem;
          }

          .why-choose-image {
            height: 400px;
          }

          .hologram-overlay {
            display: none;
          }

          .stats-badge {
            left: 50%;
            transform: translateX(-50%);
            gap: 1rem;
            padding: 1.5rem;
          }

          .stat-number {
            font-size: 1.5rem;
          }

          .stat-label {
            font-size: 0.75rem;
          }
        }
      `}</style>

      <section className="why-choose-section" id="why-choose">
        {/* Background Orbs */}
        <div className="bg-orb orb-1"></div>
        <div className="bg-orb orb-2"></div>

        <div className="why-choose-container">
          {/* Left Content */}
          <div className="why-choose-content">
            <div className="section-badge">
              <div className="badge-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                </svg>
              </div>
              WHY CHOOSE US
            </div>

            <h2 className="why-choose-title">
              The reasons to choose VAYU Lung Care
            </h2>

            <div className="reasons-list">
              {reasons.map((reason, index) => (
                <div 
                  key={index}
                  className="reason-item"
                  onMouseEnter={() => setActiveReason(index)}
                  onMouseLeave={() => setActiveReason(null)}
                >
                  <div className="number-badge">
                    {reason.number}
                  </div>

                  <div className="reason-content">
                    <h3 className="reason-title">{reason.title}</h3>
                    <p className="reason-description">{reason.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Image */}
          <div className="why-choose-image">
            <div className="decorative-dots dots-top-left"></div>
            <div className="decorative-dots dots-bottom-right"></div>
            
            <div className="image-wrapper">
              <img 
                src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=900&q=90" 
                alt="Medical team"
                className="main-image"
              />

              {/* Hologram Overlay */}
              <div className="hologram-overlay">
                <div className="hologram-content">
                  <div className="heart-pulse"></div>
                  
                  {/* Floating Medical Icons */}
                  <div className="floating-icon">
                    <Heart size={40} strokeWidth={2} fill="rgba(232, 106, 51, 0.3)" />
                  </div>
                  <div className="floating-icon">
                    <Shield size={35} strokeWidth={2} />
                  </div>
                  <div className="floating-icon">
                    <Users size={38} strokeWidth={2} />
                  </div>
                </div>
              </div>

              {/* Stats Badge */}
              <div className="stats-badge">
                <div className="stat-item">
                  <div className="stat-number">5+</div>
                  <div className="stat-label">Years</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">5K+</div>
                  <div className="stat-label">Patients</div>
                </div>
               
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default WhyChooseUsSection;